import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';
import type {
  ProviderDetail,
  ProviderReference,
  ProviderReferenceResponseSummary,
} from '../../../../../js/api';
import { ProviderReferencesService } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { CopyableText } from '../../../components/copyable-text';
import { DocumentActionDialogs, useDocumentActions } from '../../../components/document-actions';
import { DocumentMenu, useCanDownloadDocument } from '../../../components/document-type-access';
import { DOCUMENTS_QUERY_KEY } from '../../../components/documents-tab';
import { EmailContact } from '../../../components/email-contact';
import { StatusPill } from '../../../components/StatusPill';
import { WorkflowScopes } from '../../../constants/workflows';
import { useDocumentSelection } from '../../../hooks/useDocumentSelection';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { formatDateTo24Hr } from '../../../utils/date';
import { SIGNATURE_REQUEST_UPDATE_PERMISSION } from '../../../utils/permissions';
import { useAudience } from '../../../utils/workflowAccess';
import { DocumentSections, DocumentSectionSpec } from './DocumentSections';
import { EMPTY_VALUE, ProviderDrawerMaps, providerFileName, textOrDash } from './providerDisplay';
import {
  AddBar,
  EditPencil,
  FieldSpec,
  RecordCard,
  RecordSection,
} from './ProviderDrawerPrimitives';
import {
  STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY,
  STAFF_PROVIDER_QUERY_KEY,
  STAFF_PROVIDER_REFERENCES_QUERY_KEY,
} from './queryKeys';
import { ReferenceFormDialog } from './ReferenceFormDialog';
import { ReferenceScheduleDialog } from './ReferenceScheduleDialog';

const REFERENCE_DATE_FORMAT = 'MMM d, yyyy';
const REFERENCE_DATETIME_FORMAT = 'MMM d, yyyy h:mm a';
/** DocumentType slug for uploaded reference letters (uploaded on the Documents tab). */
const REFERENCE_LETTER_TYPES: DocumentSectionSpec[] = [
  {
    slug: 'reference-letter',
    title: (count) => (count === 1 ? 'Reference Letter' : 'Reference Letters'),
    testId: 'provider-reference-letters',
  },
];

// Staff-only codes (no HCO equivalent); see ProviderReferencePermissionCodes.
const CREATE_CODE = 'create__providers__providerreference';
const UPDATE_CODE = 'update__providers__providerreference';

const actionClasses = 'tw:text-xs tw:text-brand-green-0 tw:hover:underline tw:font-medium';

/**
 * A provider's references: the survey references (enter one on the provider's behalf,
 * send / re-date / re-send the request, fix a bad email, open the generated response
 * PDF) and the uploaded reference letters.
 */
export function ReferencesTab({
  provider,
}: {
  provider: ProviderDetail;
  maps: ProviderDrawerMaps;
}) {
  const providerId = provider.id;
  const queryClient = useQueryClient();
  const canCreate = useHasPermission(CREATE_CODE);
  const canUpdate = useHasPermission(UPDATE_CODE);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ProviderReference | null>(null);
  const [scheduling, setScheduling] = useState<ProviderReference | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: [STAFF_PROVIDER_REFERENCES_QUERY_KEY, providerId],
    queryFn: () => ProviderReferencesService.providerReferencesList({ provider: providerId }),
    enabled: !!providerId,
  });
  // The endpoint is unpaginated unless `limit` is passed; tolerate both shapes.
  const references: ProviderReference[] = Array.isArray(data)
    ? (data as unknown as ProviderReference[])
    : (data?.results ?? []);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_REFERENCES_QUERY_KEY, providerId] });
    queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_QUERY_KEY, providerId] });
  };

  // Heal: a completed survey with no report on file (failed render, or a bad one was
  // deleted) — re-render and attach the response PDF.
  const processMutation = useMutation({
    mutationFn: (id: string) =>
      ProviderReferencesService.providerReferencesProcessResponseCreate({ id }),
    onSuccess: () => {
      toast.success('Response report attached');
      invalidate();
      invalidateDocuments();
    },
    onError: (error: Error) =>
      handleApiError(error, { messagePrefix: 'Error processing the response' }),
  });

  const sendMutation = useMutation({
    mutationFn: ({ id, resend }: { id: string; resend: boolean }) =>
      ProviderReferencesService.providerReferencesSendRequestCreate({
        id,
        requestBody: { resend },
      }),
    onSuccess: (_data, { resend }) => {
      toast.success(resend ? 'Request re-sent' : 'Request sent');
      invalidate();
    },
    onError: (error: Error) =>
      handleApiError(error, { messagePrefix: 'Error sending the request' }),
  });

  // Survey PDFs use the same document link + paperclip control as the letters; the
  // dialogs for them render once at the bottom of the tab.
  const audience = useAudience();
  const canUpdateSignature = useHasPermission(SIGNATURE_REQUEST_UPDATE_PERMISSION);
  const viewer = { ownerId: providerId, audience, canUpdateSignature };
  // Anything that changes a document here must also refresh the references list
  // (it carries each survey's response_document) and the drawer's documents count.
  const invalidateDocuments = () => {
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY, providerId] });
    queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_REFERENCES_QUERY_KEY, providerId] });
    queryClient.invalidateQueries({
      queryKey: [STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY, providerId],
    });
  };
  const documentActions = useDocumentActions({ invalidate: invalidateDocuments });

  // A survey card is selectable when it has a response report the viewer may download
  // (the report hangs on the provider AND on its response); the selection zips those.
  const canDownload = useCanDownloadDocument(
    WorkflowScopes.Provider,
    WorkflowScopes.ProviderReferenceResponse
  );
  const reportOf = (reference: ProviderReference) => {
    const report = reference.responses?.[0]?.response_document;
    return report && canDownload(report) ? report : null;
  };
  const selection = useDocumentSelection({
    resetKey: `${providerId}|provider-references`,
    selectableIds: references.flatMap((reference) => {
      const report = reportOf(reference);
      return report ? [report.id] : [];
    }),
    zipSubject: `${providerFileName(provider)} Reference Surveys`,
    testIdPrefix: 'provider-references',
  });
  const openAddReference = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const requestFields = (
    reference: ProviderReference,
    response: ProviderReferenceResponseSummary | undefined
  ): FieldSpec[] => {
    if (!response) return [{ label: 'Request', value: 'No survey request on file' }];
    const sent = Boolean(response.request_sent_at);
    const actions: React.ReactNode[] = [];
    if (canUpdate && response.can_send_request) {
      actions.push(
        <button
          key="send"
          type="button"
          className={actionClasses}
          disabled={sendMutation.isPending}
          onClick={() => sendMutation.mutate({ id: reference.id, resend: sent })}
        >
          {sent ? 'Resend' : 'Send now'}
        </button>
      );
      if (!sent) {
        actions.push(
          <button
            key="schedule"
            type="button"
            className={actionClasses}
            onClick={() => setScheduling(reference)}
          >
            Change date
          </button>
        );
      }
    }
    let requestState: string;
    if (sent) {
      requestState = `Sent ${formatDateTo24Hr(response.request_sent_at, REFERENCE_DATETIME_FORMAT)}`;
    } else if (response.request_scheduled_at) {
      requestState = `Scheduled for ${formatDateTo24Hr(response.request_scheduled_at, REFERENCE_DATE_FORMAT)}`;
    } else {
      requestState = 'Not scheduled';
    }
    const fields: FieldSpec[] = [
      {
        label: 'Survey',
        value: (
          <StatusPill
            scope={WorkflowScopes.ProviderReferenceResponse}
            status={response.current_status}
            small
          />
        ),
      },
      {
        label: 'Request',
        value: (
          <span className="tw:flex tw:flex-col tw:gap-0.5">
            <span>{requestState}</span>
            {actions.length > 0 && <span className="tw:flex tw:gap-3">{actions}</span>}
          </span>
        ),
      },
    ];
    return fields;
  };

  /** Bottom-right of a survey card: the heal button and/or the report's paperclip. */
  const surveyFooter = (
    reference: ProviderReference,
    response: ProviderReferenceResponseSummary | undefined
  ): React.ReactNode => {
    if (!response) return null;
    const completed = response.current_status?.code === 'COMPLETED';
    const items: React.ReactNode[] = [];
    if (canUpdate && completed && !response.response_document) {
      items.push(
        <Button
          key="process"
          color="brand-green-outline"
          small
          onClick={() => processMutation.mutate(reference.id)}
          disabled={processMutation.isPending}
          title="Render and attach the response report"
          data-testid={`process-response-${reference.id}`}
        >
          <i className="fas fa-fw fa-file-pdf" /> Process Response
        </Button>
      );
    }
    if (response.response_document) {
      items.push(
        <DocumentMenu
          key="menu"
          doc={response.response_document}
          viewer={viewer}
          actions={documentActions}
        />
      );
    }
    return items.length ? <>{items}</> : null;
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      {isLoading && <div className="tw:text-sm tw:text-zinc-500">Loading references...</div>}
      {error ? (
        <div className="tw:text-sm tw:text-stoplight-danger">Unable to load references.</div>
      ) : null}

      {/* Nothing on file: a one-button bar (or a plain line), never an empty section. */}
      {!isLoading &&
        !error &&
        references.length === 0 &&
        (canCreate ? (
          <AddBar
            lead="No reference surveys on file — add:"
            data-testid="provider-references-add-bar"
          >
            <Button
              type="button"
              color="brand-green-outline"
              small
              onClick={openAddReference}
              data-testid="add-reference"
            >
              <i className="fas fa-fw fa-plus" aria-hidden="true" /> Reference
            </Button>
          </AddBar>
        ) : (
          <div className="tw:text-sm tw:text-zinc-500" data-testid="provider-references-empty">
            No reference surveys on file.
          </div>
        ))}

      {!isLoading && !error && references.length > 0 && (
        <RecordSection
          title={references.length === 1 ? 'Reference Survey' : 'Reference Surveys'}
          count={references.length}
          emptyText={null}
          leading={selection.renderGroupCheckbox(
            references.flatMap((reference) => {
              const report = reportOf(reference);
              return report ? [report.id] : [];
            }),
            'reference survey reports',
            'provider-references-select-all-surveys'
          )}
          action={
            canCreate ? (
              <button
                type="button"
                className="tw:text-brand-green-0 tw:hover:underline"
                onClick={openAddReference}
                data-testid="add-reference"
              >
                <i className="fas fa-plus tw:mr-1" aria-hidden="true" />
                Add one
              </button>
            ) : null
          }
          data-testid="provider-references"
        >
          {references.map((reference) => {
            const isTerminated = Boolean(reference.terminated_at);
            const response = reference.responses?.[0];
            const report = reportOf(reference);
            return (
              <RecordCard
                key={reference.id}
                muted={isTerminated}
                leading={
                  report
                    ? selection.renderCheckbox(
                        report.id,
                        `${reference.full_name || 'reference'} response report`
                      )
                    : null
                }
                title={
                  <span className="tw:flex tw:items-center tw:gap-2">
                    {textOrDash(reference.full_name)}
                    {canUpdate && (
                      <EditPencil
                        onClick={() => {
                          setEditing(reference);
                          setFormOpen(true);
                        }}
                        title="Edit reference"
                        aria-label={`Edit ${reference.full_name}`}
                        data-testid={`edit-reference-${reference.id}`}
                      />
                    )}
                  </span>
                }
                rightElement={
                  reference.current_status ? (
                    <StatusPill
                      scope={WorkflowScopes.ProviderReference}
                      status={reference.current_status}
                      small
                    />
                  ) : null
                }
                fields={[
                  // title / phone are optional on a reference: omit the row when blank
                  ...(reference.title ? [{ label: 'Title', value: reference.title }] : []),
                  {
                    label: 'Email',
                    value: reference.email ? <EmailContact email={reference.email} /> : EMPTY_VALUE,
                  },
                  ...(reference.phone_number
                    ? [{ label: 'Phone', value: <CopyableText value={reference.phone_number} /> }]
                    : []),
                  {
                    label: 'Effective',
                    value:
                      formatDateTo24Hr(reference.effective_at, REFERENCE_DATE_FORMAT) ||
                      EMPTY_VALUE,
                  },
                  ...(isTerminated
                    ? [
                        {
                          label: 'Terminated',
                          value:
                            formatDateTo24Hr(reference.terminated_at, REFERENCE_DATE_FORMAT) ||
                            EMPTY_VALUE,
                        },
                      ]
                    : []),
                  ...requestFields(reference, response),
                ]}
                footer={surveyFooter(reference, response)}
              />
            );
          })}
        </RecordSection>
      )}

      {/* Uploaded letters: the standard document sections (cards once there are letters,
          a one-button upload bar until then). */}
      <DocumentSections
        providerId={providerId}
        types={REFERENCE_LETTER_TYPES}
        testIdPrefix="provider-reference-letters"
        zipSubject={`${providerFileName(provider)} Reference Letters`}
        noneOnFileLead="No reference letters on file — add:"
        emptyText="No reference letters on file."
        onDocumentsChanged={invalidateDocuments}
      />

      {selection.bar}

      <ReferenceFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        providerId={providerId}
        reference={editing}
      />
      <ReferenceScheduleDialog
        open={scheduling !== null}
        onClose={() => setScheduling(null)}
        providerId={providerId}
        reference={scheduling}
      />
      <DocumentActionDialogs actions={documentActions} audience={audience} />
    </div>
  );
}
